import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  SavedTableLayout,
  SaveTableLayoutInput,
  TableLayoutStatus,
} from "../types";

// Mirrors the GraphQL `TableLayout` type (services/src/modules/table-layout/graphql.ts).
const TABLE_LAYOUT_FIELDS = `
  id
  organizationId
  tableKey
  name
  scope
  status
  sharedWith { userId role }
  createdBy
  createdByName
  visibleColumns
  columnWidths
  sortColumns { columnKey direction }
  columnOrder
  rowsPerPage
  filters
  searchText
  createdAt
  updatedAt
`;

export const tableLayoutApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getTableLayouts: builder.query<SavedTableLayout[], { tableKey: string }>({
      query: ({ tableKey }) => ({
        body: {
          query: `
            query TableLayouts($tableKey: String!) {
              tableLayouts(tableKey: $tableKey) {
                ${TABLE_LAYOUT_FIELDS}
              }
            }
          `,
          variables: { tableKey },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<SavedTableLayout[]>(res, "tableLayouts"),

      providesTags: (result, _err, { tableKey }) =>
        result
          ? [
              ...result.map((l) => ({ type: "TableLayout" as const, id: l.id })),
              { type: "TableLayout" as const, id: `LIST:${tableKey}` },
            ]
          : [{ type: "TableLayout" as const, id: `LIST:${tableKey}` }],
    }),

    saveTableLayout: builder.mutation<
      SavedTableLayout,
      { tableKey: string; input: SaveTableLayoutInput }
    >({
      query: ({ input }) => ({
        body: {
          query: `
            mutation SaveTableLayout($input: SaveTableLayoutInput!) {
              saveTableLayout(input: $input) {
                ${TABLE_LAYOUT_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<SavedTableLayout>(res, "saveTableLayout"),

      invalidatesTags: (result, _err, { tableKey }) => [
        { type: "TableLayout", id: `LIST:${tableKey}` },
        ...(result ? [{ type: "TableLayout" as const, id: result.id }] : []),
        // Overwriting/creating shared layouts changes org usage counts.
        { type: "Organization", id: "LIST" },
      ],
    }),

    setTableLayoutStatus: builder.mutation<
      SavedTableLayout,
      { id: string; status: TableLayoutStatus }
    >({
      query: ({ id, status }) => ({
        body: {
          query: `
            mutation SetTableLayoutStatus($id: String!, $status: String!) {
              setTableLayoutStatus(id: $id, status: $status) {
                ${TABLE_LAYOUT_FIELDS}
              }
            }
          `,
          variables: { id, status },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<SavedTableLayout>(res, "setTableLayoutStatus"),

      invalidatesTags: (result) =>
        result
          ? [
              { type: "TableLayout", id: result.id },
              { type: "TableLayout", id: `LIST:${result.tableKey}` },
            ]
          : [{ type: "TableLayout", id: "LIST" }],
    }),

    deleteTableLayout: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteTableLayout($id: String!) {
              deleteTableLayout(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteTableLayout"),

      invalidatesTags: (_result, _err, id) => [
        { type: "TableLayout", id },
        { type: "TableLayout", id: "LIST" },
        { type: "Organization", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetTableLayoutsQuery,
  useLazyGetTableLayoutsQuery,
  useSaveTableLayoutMutation,
  useSetTableLayoutStatusMutation,
  useDeleteTableLayoutMutation,
} = tableLayoutApi;
